import React, { useEffect, useRef, useState } from "react";
import { Upload, FileText, Eye, CheckCircle2, Sparkles, Trash2, ShieldCheck, X } from "lucide-react";
import { toast } from "sonner";
import { boardFetch, fileUrl, BOARD_API, getToken } from "../api";
import { useCompany, SectionHeader, MarkdownLite, Modal } from "../components";

const Documents = () => {
  const { company } = useCompany();
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [detail, setDetail] = useState(null); // {doc, acks, summaryLoading}
  const fileRef = useRef();
  const [title, setTitle] = useState("");

  const load = () => boardFetch(`/companies/${company.id}/documents`).then(setDocs).catch((e) => toast.error(e.message));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [company.id]);

  const onUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", title.trim() || file.name);
      fd.append("category", "Board-Pack");
      const res = await fetch(`${BOARD_API}/companies/${company.id}/documents`, { method: "POST", headers: { Authorization: `Bearer ${getToken()}` }, body: fd });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Upload fehlgeschlagen");
      toast.success("Dokument hochgeladen");
      setTitle("");
      load();
    } catch (err) { toast.error(err.message); } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const openDetail = async (doc) => {
    const acks = await boardFetch(`/documents/${doc.id}/acknowledgments`);
    setDetail({ doc, acks });
  };
  const refreshDetail = async (doc) => {
    const [fresh, acks] = await Promise.all([
      boardFetch(`/companies/${company.id}/documents`).then((l) => l.find((d) => d.id === doc.id)),
      boardFetch(`/documents/${doc.id}/acknowledgments`),
    ]);
    setDocs(await boardFetch(`/companies/${company.id}/documents`));
    setDetail({ doc: fresh || doc, acks });
  };

  const acknowledge = async (doc, action) => {
    await boardFetch(`/documents/${doc.id}/acknowledge`, { method: "POST", json: { action } });
    toast.success(action === "approved" ? "Freigabe protokolliert" : "Als gelesen bestätigt");
    refreshDetail(doc);
  };

  const summarize = async (doc) => {
    setDetail((d) => ({ ...d, summaryLoading: true }));
    try {
      const res = await boardFetch(`/documents/${doc.id}/ai/summary`, { method: "POST" });
      setDetail((d) => ({ ...d, doc: { ...d.doc, summary: res.summary }, summaryLoading: false }));
    } catch (e) { toast.error(e.message); setDetail((d) => ({ ...d, summaryLoading: false })); }
  };

  const remove = async (doc) => { await boardFetch(`/documents/${doc.id}`, { method: "DELETE" }); setDetail(null); load(); };

  return (
    <div>
      <SectionHeader overline="Sicherer Datenraum" title="Dokumente" action={
        <div className="flex items-center gap-3">
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Titel (optional)" data-testid="doc-title"
            className="border border-white/15 bg-black/30 px-3 py-2.5 text-sm text-white outline-none focus:border-champagne" />
          <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="doc-upload-btn"
            className="flex items-center gap-2 bg-champagne px-4 py-2.5 text-xs uppercase tracking-widest text-obsidian hover:bg-champagne-light disabled:opacity-50">
            <Upload size={15} /> {uploading ? "Lädt…" : "Hochladen"}
          </button>
          <input ref={fileRef} type="file" accept=".pdf,.txt,.md,.docx" className="hidden" onChange={onUpload} data-testid="doc-file-input" />
        </div>
      } />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="documents-grid">
        {docs.length === 0 && <p className="col-span-full py-10 text-center text-white/35">Noch keine Dokumente</p>}
        {docs.map((doc) => (
          <div key={doc.id} className="flex flex-col border border-white/10 bg-white/[0.02] p-5" data-testid="document-card">
            <div className="flex items-start gap-3">
              <FileText size={22} className="mt-0.5 shrink-0 text-champagne" strokeWidth={1.4} />
              <div className="min-w-0">
                <p className="truncate text-sm text-white">{doc.title}</p>
                <p className="truncate text-xs text-white/35">{doc.filename} · {(doc.size / 1024).toFixed(0)} KB</p>
              </div>
            </div>
            {doc.summary && <span className="mt-3 inline-flex w-fit items-center gap-1 rounded-full border border-champagne/30 bg-champagne/10 px-2 py-0.5 text-[10px] uppercase tracking-wider text-champagne"><Sparkles size={11} /> KI-Zusammenfassung</span>}
            <div className="mt-4 flex items-center gap-4 border-t border-white/10 pt-3 text-xs">
              <a href={fileUrl(doc.id)} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-white/55 hover:text-champagne" data-testid="doc-view"><Eye size={14} /> Ansehen</a>
              <button onClick={() => openDetail(doc)} className="flex items-center gap-1 text-white/55 hover:text-champagne" data-testid="doc-detail"><ShieldCheck size={14} /> Freigaben</button>
            </div>
          </div>
        ))}
      </div>

      {detail && (
        <Modal title={detail.doc.title} onClose={() => setDetail(null)}>
          <div className="space-y-5">
            <div className="flex flex-wrap gap-3">
              <a href={fileUrl(detail.doc.id)} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 border border-white/20 px-4 py-2 text-xs uppercase tracking-widest text-white/75 hover:text-white"><Eye size={14} /> Öffnen</a>
              <button onClick={() => acknowledge(detail.doc, "read")} data-testid="ack-read" className="flex items-center gap-1.5 border border-white/20 px-4 py-2 text-xs uppercase tracking-widest text-white/75 hover:text-white"><CheckCircle2 size={14} /> Gelesen</button>
              <button onClick={() => acknowledge(detail.doc, "approved")} data-testid="ack-approve" className="flex items-center gap-1.5 bg-emerald-500/20 px-4 py-2 text-xs uppercase tracking-widest text-emerald-300 hover:bg-emerald-500/30"><CheckCircle2 size={14} /> Freigeben</button>
              <button onClick={() => summarize(detail.doc)} data-testid="ai-summary-btn" className="flex items-center gap-1.5 bg-champagne px-4 py-2 text-xs uppercase tracking-widest text-obsidian hover:bg-champagne-light"><Sparkles size={14} /> KI-Zusammenfassung</button>
              <button onClick={() => remove(detail.doc)} className="ml-auto flex items-center gap-1.5 text-xs uppercase tracking-widest text-red-400/70 hover:text-red-400"><Trash2 size={14} /> Löschen</button>
            </div>

            {detail.summaryLoading && <p className="animate-pulse text-sm text-white/40">KI fasst das Dokument zusammen…</p>}
            {detail.doc.summary && !detail.summaryLoading && (
              <div className="border border-champagne/25 bg-champagne/[0.03] p-4" data-testid="doc-summary"><MarkdownLite text={detail.doc.summary} /></div>
            )}

            <div>
              <h4 className="mb-3 text-xs uppercase tracking-[0.25em] text-white/45">Freigabe- / Lese-Protokoll</h4>
              <div className="space-y-2" data-testid="ack-list">
                {detail.acks.length === 0 && <p className="text-sm text-white/35">Noch keine Bestätigungen</p>}
                {detail.acks.map((a) => (
                  <div key={a.id} className="flex items-center justify-between border border-white/10 px-3 py-2 text-xs">
                    <span className={a.action === "approved" ? "text-emerald-300" : "text-white/70"}>{a.action === "approved" ? "Freigegeben" : "Gelesen"} · {a.user}</span>
                    <span className="text-white/35">{new Date(a.at).toLocaleString("de-CH")}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Documents;
